/**
 * CR V3 Web 观众 WebSocket 客户端：join + getSessionData + liveSpeech + envelope history
 */

import { ref } from 'vue'
import {
  liveSpeechToViewerUpdate,
  sessionSnapshotToLanguages,
  shortLangCode,
} from '../adapters/live-speech-adapter'
import { fetchJoinInfo, fetchWsToken } from '../api/cr-v3-viewer.api'
import type {
  ClearSpeechHistoryMessage,
  CrV3SessionSnapshot,
  LiveSpeechMessage,
  ViewerSubtitleRow,
} from '../types'
import type { ViewerLanguageItem } from '@/modules/speech-translation/api/viewer'
import { MAX_DISPLAY_LINES } from '@/modules/speech-translation/constants'
import { applyViewerUpdate } from '../adapters/live-speech-adapter'
import {
  subtitleStyleFromSession,
  subtitleStyleFromUpdateMessage,
} from './subtitle-style-feed'
import { mergeSpeechHistoryByUtterance } from '../utils/merge-speech-history'
import { mergePostClassSessionFields } from '../utils/build-post-class-qr-items'

export interface CrV3SessionLanguagesPatch {
  sourceLanguage: ViewerLanguageItem
  targetLanguages: ViewerLanguageItem[]
  classroomWorkLangCode: string
}

export interface CrV3ViewerFeedCallbacks {
  onInit: (data: {
    fragments: ViewerSubtitleRow[]
    preview: ViewerSubtitleRow | null
    sourceLanguage: ViewerLanguageItem
    targetLanguages: ViewerLanguageItem[]
    classroomWorkLangCode?: string
    subtitleStyle?: Record<string, unknown>
  }) => void
  onPreview: (row: ViewerSubtitleRow | null) => void
  onFragment: (row: ViewerSubtitleRow) => void
  onError: (message: string) => void
  onLoading: (loading: boolean) => void
  onSubtitleStyleUpdate?: (style: Record<string, unknown>) => void
  /** 多语种 history 合并前清空 UI */
  onHistoryCleared?: () => void
  /** lang/select 或 clearSpeechHistory 后刷新 session 语种 */
  onSessionLanguagesChanged?: (langs: CrV3SessionLanguagesPatch) => void
  /** 课后 / session 快照（getSessionDataResult + updateSessionData 浅合并） */
  onSessionSnapshot?: (session: CrV3SessionSnapshot) => void
}

export interface UseCrV3ViewerWsOptions {
  meetingCode: string
  viewerName: string
  languageCode: string
  callbacks: CrV3ViewerFeedCallbacks
}

/** 建立 WS 连接并处理 Gateway 信令 */
export function useCrV3ViewerWs(options: UseCrV3ViewerWsOptions) {
  const connected = ref(false)
  let ws: WebSocket | null = null

  const fragments: ViewerSubtitleRow[] = []
  let preview: ViewerSubtitleRow | null = null
  /** 会话快照（课后字段浅合并，对齐 Android mergeSessionData） */
  let sessionSnapshot: CrV3SessionSnapshot = {}

  let targetLanguageCodes: string[] = []
  let historyFetchGeneration = 0
  let pendingHistoryRequests = 0
  /** 最近一次 session 源/目标语短码，用于区分 updateSessionData 副作用 */
  let trackedSourceShort = ''
  let trackedTargetShorts: string[] = []

  function syncTrackedSessionLangs(sourceShort: string, targetShorts: string[]) {
    trackedSourceShort = sourceShort
    trackedTargetShorts = targetShorts
  }

  function applyHeldSessionSnapshot(patch: CrV3SessionSnapshot, mode: 'merge' | 'replace' = 'merge') {
    sessionSnapshot =
      mode === 'replace'
        ? mergePostClassSessionFields({}, patch)
        : mergePostClassSessionFields(sessionSnapshot, patch)
    options.callbacks.onSessionSnapshot?.(sessionSnapshot)
  }

  function sendJson(type: string, data: Record<string, unknown> = {}) {
    if (ws?.readyState !== WebSocket.OPEN) {
      return
    }
    ws.send(JSON.stringify({ type, data }))
  }

  function clearSubtitleBuffer() {
    fragments.length = 0
    preview = null
    options.callbacks.onHistoryCleared?.()
  }

  function applySessionLanguagesFromSnapshot(session: CrV3SessionSnapshot) {
    const langs = sessionSnapshotToLanguages(session)
    options.callbacks.onSessionLanguagesChanged?.({
      sourceLanguage: langs.sourceLanguage,
      targetLanguages: langs.targetLanguages,
      classroomWorkLangCode: langs.classroomWorkLangCode,
    })
    return langs
  }

  function requestSpeechHistory(codes: string[]) {
    const normalized = [...new Set(codes.map((c) => shortLangCode(c)).filter(Boolean))]
    if (!normalized.length) {
      return
    }
    historyFetchGeneration += 1
    clearSubtitleBuffer()
    targetLanguageCodes = normalized
    // D10：后端返回全量 envelope 列表，一次请求即可
    pendingHistoryRequests = 1
    sendJson('getSpeechHistory', { languageCode: normalized[0] })
  }

  function tryApplyMergedHistory(raw: unknown, generation: number) {
    if (generation !== historyFetchGeneration) {
      return
    }
    pendingHistoryRequests -= 1
    if (pendingHistoryRequests > 0) {
      return
    }
    const merged = mergeSpeechHistoryByUtterance(
      Array.isArray(raw) ? raw : [],
      targetLanguageCodes,
    )
    fragments.length = 0
    fragments.push(...merged.slice(-MAX_DISPLAY_LINES))
    preview = null
    for (const row of fragments) {
      options.callbacks.onFragment(row)
    }
    options.callbacks.onPreview(null)
  }

  function handleSpeechHistoryResult(raw: unknown, generation: number) {
    tryApplyMergedHistory(raw, generation)
  }

  function handleClearSpeechHistory(_payload: ClearSpeechHistoryMessage) {
    clearSubtitleBuffer()
    sendJson('getSessionData', {})
  }

  function handleLiveSpeech(payload: LiveSpeechMessage) {
    const update = liveSpeechToViewerUpdate(payload)
    if (!update) {
      return
    }
    const next = applyViewerUpdate({ fragments, preview }, update, MAX_DISPLAY_LINES)
    fragments.length = 0
    fragments.push(...next.fragments)
    preview = next.preview
    if (update.kind === 'preview') {
      options.callbacks.onPreview(preview)
    } else {
      options.callbacks.onFragment(update.row)
      options.callbacks.onPreview(null)
    }
  }

  function handleMessage(raw: string) {
    let message: { type?: string; data?: unknown }
    try {
      message = JSON.parse(raw)
    } catch {
      return
    }
    const type = message.type
    if (type === 'liveSpeech') {
      handleLiveSpeech(message as LiveSpeechMessage)
      return
    }
    if (type === 'clearSpeechHistory') {
      handleClearSpeechHistory(message as ClearSpeechHistoryMessage)
      return
    }
    if (type === 'getSessionDataResult') {
      const session = (message.data ?? {}) as CrV3SessionSnapshot
      applyHeldSessionSnapshot(session, 'replace')
      const langs = applySessionLanguagesFromSnapshot(session)
      syncTrackedSessionLangs(
        langs.sourceLanguage.languageCode,
        langs.targetLanguages.map((t) => t.languageCode),
      )
      requestSpeechHistory(langs.targetLanguages.map((t) => t.languageCode))
      options.callbacks.onInit({
        fragments: [...fragments],
        preview,
        sourceLanguage: langs.sourceLanguage,
        targetLanguages: langs.targetLanguages,
        classroomWorkLangCode: langs.classroomWorkLangCode,
        subtitleStyle: subtitleStyleFromSession(session),
      })
      return
    }
    if (type === 'updateSessionData') {
      const session = (message.data ?? {}) as CrV3SessionSnapshot
      applyHeldSessionSnapshot(session)
      if (
        session.sourceLanguage != null
        || session.targetLanguage != null
        || session.workLangCode != null
        || session.workLangListenCode != null
      ) {
        const langs = applySessionLanguagesFromSnapshot(session)
        // D15 目标语-only：Redis 保留，仅刷新列配置；源语 reset 走 clearSpeechHistory
        syncTrackedSessionLangs(
          langs.sourceLanguage.languageCode,
          langs.targetLanguages.map((t) => t.languageCode),
        )
      }
      return
    }
    if (type === 'updateSubtitleStyle') {
      const style = subtitleStyleFromUpdateMessage(message.data)
      if (style && options.callbacks.onSubtitleStyleUpdate) {
        options.callbacks.onSubtitleStyleUpdate(style)
      }
      return
    }
    if (type === 'joinResult') {
      const data = message.data as { success?: boolean; errorCode?: string; message?: string }
      if (data?.success) {
        sendJson('getSessionData', {})
      } else {
        options.callbacks.onError(data?.message || 'join failed')
      }
      return
    }
    if (type === 'getSpeechHistoryResult') {
      handleSpeechHistoryResult(message.data, historyFetchGeneration)
      return
    }
    if (type === 'ping') {
      sendJson('pong')
    }
  }

  async function connect() {
    options.callbacks.onLoading(true)
    try {
      const joinInfo = await fetchJoinInfo(options.meetingCode)
      if (joinInfo.remainingSlots <= 0) {
        options.callbacks.onError('会议人数已满')
        options.callbacks.onLoading(false)
        return
      }

      const tokenData = await fetchWsToken(options.meetingCode)
      const url = `${tokenData.wsUrl}?token=${encodeURIComponent(tokenData.token)}`
      ws = new WebSocket(url)

      ws.onopen = () => {
        connected.value = true
        options.callbacks.onLoading(false)
        sendJson('join', {
          name: options.viewerName,
          languageCode: options.languageCode,
          role: 'viewer',
        })
        // getSessionData 在 joinResult.success 后发送（D12）
      }

      ws.onmessage = (ev) => {
        if (typeof ev.data === 'string') {
          handleMessage(ev.data)
        }
      }

      ws.onerror = () => {
        options.callbacks.onError('WebSocket 连接异常')
      }

      ws.onclose = () => {
        connected.value = false
        options.callbacks.onLoading(false)
      }
    } catch (err) {
      options.callbacks.onLoading(false)
      options.callbacks.onError(err instanceof Error ? err.message : '连接失败')
    }
  }

  function disconnect() {
    ws?.close()
    ws = null
    connected.value = false
    pendingHistoryRequests = 0
    targetLanguageCodes = []
  }

  return { connect, disconnect, connected }
}
